<template>
  <div>
    <LoginAppLoader v-if="route.name !== ROUTE_NAMES.WEB_APP_AFTER_LOGIN_GATEWAY" />

    <router-view />

    <AppNotifications />
  </div>
</template>

<script setup>
import { useAuth } from '@clerk/vue'
import { jwtDecode } from 'jwt-decode'
import { isUndefined } from 'lodash'
import { onBeforeUnmount, onMounted, watch, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import { isSafari, STYLED_SCROLLBAR_CLASS } from '@/utils/general'
import { isWebAppInjectionKey } from '@/utils/injection-keys'
import { riseLSWarning } from '@/utils/persist'
import LoginAppLoader from '@web-app/login-app/LoginAppLoader'
import { useLoginAppStores } from '@web-app/utils/login-app-stores'

import AppNotifications from '@/components/ui/AppNotifications/AppNotifications'

defineOptions({
  name: 'LoginApp',
  inheritAttrs: false
})

provide(isWebAppInjectionKey, true)

useLoginAppStores()
const { isLoaded: authProviderLoaded, isSignedIn, signOut } = useAuth()

if (!isSafari()) {
  document.body.classList.add(STYLED_SCROLLBAR_CLASS)
}

const router = useRouter()

onMounted(() => {
  riseLSWarning()
})

const route = useRoute()

const CLERK_TICKET_QUERY_KEY = '__clerk_ticket'

const isAcceptInvitationFlow = (ticket = '') => {
  if (!ticket) return false

  let decoded = null

  try {
    decoded = jwtDecode(ticket)
  } catch (error) {
    console.error('Failed to decode JWT:', error)
  }

  return decoded && decoded?.st === 'organization_invitation'
}

const CLERK_STATUS_QUERY_KEY = '__clerk_status'

const isVerifiedSignUpFlow = (clerkStatus = '') => {
  if (!clerkStatus) return false

  return clerkStatus === 'verified'
}

watch(
  isSignedIn,
  async (newValue, oldValue) => {
    if (!authProviderLoaded.value || isUndefined(newValue)) return
    if (newValue === oldValue) return

    if (newValue === false && (route.meta.shouldRedirectToLogin ?? true)) {
      await router.push({ name: ROUTE_NAMES.WEB_APP_LOGIN })
      return
    }

    if (newValue && isAcceptInvitationFlow(route?.query?.[CLERK_TICKET_QUERY_KEY])) {
      await signOut.value({
        redirectUrl: route.fullPath
      })

      return
    }

    if (
      route.name === ROUTE_NAMES.WEB_APP_SIGN_UP &&
      isVerifiedSignUpFlow(route?.query?.[CLERK_STATUS_QUERY_KEY])
    ) {
      return
    }

    if (newValue && (route.meta.shouldRedirectToGateway ?? true)) {
      await router.push({ name: ROUTE_NAMES.WEB_APP_AFTER_LOGIN_GATEWAY })
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (document.body.classList.contains(STYLED_SCROLLBAR_CLASS)) {
    document.body.classList.remove(STYLED_SCROLLBAR_CLASS)
  }
})
</script>

<style lang="scss">
@import '~@/assets/styles/mixins';

html,
body,
#app {
  @include styled-native-scrollbar(8, $grey-2, $grey-10, true);
}
</style>

<style lang="scss" scoped></style>
